/**
 * @architecture-header 1
 * @layer module
 * @domain help
 * @responsibility Author the static, in-bundle zh-TW help article content SH-F06 searches and
 *   ranks -- no backend, no network, no CMS (CONTRACT APPROVED R1). Article content is a product
 *   claim, not prose: every named control/label/flow an article cites must exist in the live app
 *   exactly as described (SH-F06 round-1 finding 1), and a citation covering a control whose
 *   visible text differs by viewport (e.g. room-manage's remove button) must name every variant
 *   actually shown, not just the desktop one (round-2 finding 1) -- articles.test.ts extracts
 *   every quoted citation directly from each article's own live body text (round-2 finding 2) and
 *   checks it BY CONSTRUCTION, by iterating this module's own exported helpArticles, not a
 *   hand-picked subset (round-3 finding 1: rep-view quoted 「報告」 with zero coverage because it was
 *   never added to the old subset -- the same class of gap can no longer exist, since an article
 *   this test never iterates cannot exist). act-review, live-alert and notifications-basics
 *   describe behavior/affordances rather than a quotable control label, so they carry nothing for
 *   the guard to check -- re-review their wording against the live UI whenever the surface they
 *   describe changes (ClassStudentsTab.tsx's context menu, the live intervention seat panel, or
 *   MobileTopBar/ShellPopovers' inbox icon), the same "at minimum a review note" bar the round-1
 *   adjudication set.
 * @state-owner none -- static data, versioned with the frontend bundle like any other UI copy
 * @allowed-imports shared workspace section type
 * @forbidden network; database access; React
 * @public-api modules/help
 * @side-effects none
 * @tests search.test.ts; content/articles.test.ts
 */

import type { WorkspaceSection } from '../../../shared/lib/model';

export interface HelpArticle {
  id: string;
  title: string;
  body: string;
  sections: readonly WorkspaceSection[];
  keywords: readonly string[];
}

export const helpArticles: readonly HelpArticle[] = [
  {
    id: 'act-create',
    title: '建立新活動',
    body: '在「活動」分頁點擊「新增活動」，選擇題型後即可開始編輯。活動會立即以草稿狀態儲存，發布後學生才看得到。',
    sections: ['act'],
    keywords: ['活動', '建立', '新增', '測驗', '題目'],
  },
  {
    id: 'act-review',
    title: '批改與查看學生作答',
    body: '在活動列表點擊任一活動即可查看學生的作答狀況，系統會標示出尚待批改的題目與常見錯誤選項。',
    sections: ['act'],
    keywords: ['批改', '作答', '評分', '活動'],
  },
  {
    id: 'room-invite',
    title: '邀請學生加入班級',
    body: '在「班級」分頁開啟班級詳情，點擊工具列的「班級加入連結」即可看到 QR Code 與可複製的網址；把連結或 QR Code 分享給學生，學生登入後點擊連結就會自動加入班級。要管理個別邀請時，改用旁邊的「管理邀請」。',
    sections: ['room'],
    keywords: ['班級', '邀請', '加入', '連結', '學生'],
  },
  {
    id: 'room-manage',
    title: '管理班級成員',
    body: '班級詳情頁的「成員」分頁列出所有已加入的學生，點擊成員列上的移除按鈕即可把學生移出班級——桌面版按鈕文字為「移除成員」，手機版則顯示「移除」；點擊學生列則可開啟個人詳情，或用右鍵選單的「檢視學習報告」查看個別學習紀錄。',
    sections: ['room'],
    keywords: ['班級', '成員', '學生', '移除成員', '移除', '學習報告'],
  },
  {
    id: 'live-start',
    title: '開始即時上課',
    body: '從「活動」分頁選擇要上的教材並點擊「發放」，在跳出的視窗把「方式」選為「課堂進行」，再點擊「開始課堂進行」；系統會建立即時課堂並自動切換到「即時」分頁，顯示每位學生目前作答到哪一題。',
    sections: ['live'],
    keywords: ['即時', '上課', '課堂進行', '發放'],
  },
  {
    id: 'live-alert',
    title: '了解學生狀態警示',
    body: '即時上課畫面中，學生座位上出現的提示代表系統偵測到可能需要協助的狀況，點擊座位即可查看詳情並介入。',
    sections: ['live'],
    keywords: ['即時', '警示', '狀態', '介入'],
  },
  {
    id: 'rep-view',
    title: '查看學生學習報告',
    body: '在「報告」分頁選擇班級或個別學生，即可查看歷次活動的作答統計與需要加強的知識點。',
    sections: ['rep'],
    keywords: ['報告', '成績', '進度', '知識點'],
  },
  {
    id: 'notifications-basics',
    title: '收件匣與通知',
    body: '點擊工作列的信封圖示可以開啟收件匣，查看班級加入等通知；點擊單一通知會標記為已讀並前往相關頁面。',
    sections: ['act', 'room', 'live', 'rep'],
    keywords: ['通知', '收件匣', '未讀', '已讀'],
  },
  {
    id: 'account-settings',
    title: '更新帳號與偏好設定',
    body: '點擊右上角的帳號頭像並選擇「設定」，即可更新個人資料、通知偏好與介面語言等選項。',
    sections: ['act', 'room', 'live', 'rep'],
    keywords: ['設定', '帳號', '偏好', '語言'],
  },
] as const;
